import { authedFetch } from '../../lib/authedFetch';
import { NRC_FEED_MAX_BYTES, isNrcFeedReceipt, nrcReceiptIsFresh, validateNrcNationalFeed, type NrcNationalFeed } from '../../lib/nrcNationalFeed';

export type { NrcNationalFeed } from '../../lib/nrcNationalFeed';
type Loader = (signal?: AbortSignal) => Promise<NrcNationalFeed>;
let injectedLoader: Loader | null = null;

/** Server/job entrypoints statically inject this loader, so esbuild includes
 * the GCS implementation while the browser bundle contains no server SDK. */
export function setNrcNationalFeedServerLoader(loader: Loader): void { injectedLoader = loader; }
async function importByPath(path: string): Promise<{ getNrcNationalFeed: Loader }> {
  return import(/* @vite-ignore */ path) as Promise<{ getNrcNationalFeed: Loader }>;
}
export async function loadNrcNationalFeed(signal?: AbortSignal): Promise<NrcNationalFeed> {
  signal?.throwIfAborted();
  if (typeof window === 'undefined') {
    const loader = injectedLoader ?? (await importByPath('../../server/refdata/nrcNationalFeedCache')).getNrcNationalFeed;
    return loader(signal);
  }
  const response = await authedFetch('/api/refdata/nrc-reactor-status/feed', { signal, cache: 'no-store' });
  if (!response.ok || !response.body) throw new Error('Verified NRC national feed is unavailable');
  const reader = response.body.getReader();
  const chunks: Uint8Array[] = []; let length = 0;
  try {
    while (true) {
      const next = await reader.read(); if (next.done) break;
      length += next.value.length;
      // JSON escaping can expand the ASCII feed. This is a wire-size ceiling;
      // the decoded original body has a separate strict 4 MiB bound below.
      if (length > NRC_FEED_MAX_BYTES * 2 + 64 * 1024) throw new Error('NRC cache response exceeds its bound');
      chunks.push(next.value);
    }
  } finally { await reader.cancel().catch(() => {}); }
  const bytes = new Uint8Array(length); let offset = 0;
  for (const chunk of chunks) { bytes.set(chunk, offset); offset += chunk.length; }
  const value: unknown = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes));
  const feed = value as NrcNationalFeed | null;
  if (!feed || typeof feed.text !== 'string' || !isNrcFeedReceipt(feed.provenance) || !nrcReceiptIsFresh(feed.provenance)
    || !['gcs-hit', 'refreshed'].includes(feed.provenance.cacheStatus)) throw new Error('NRC cache provenance is invalid or stale');
  const raw = new TextEncoder().encode(feed.text);
  const hash = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', raw)), value => value.toString(16).padStart(2, '0')).join('');
  const counts = validateNrcNationalFeed(feed.text);
  if (raw.length !== feed.provenance.object.bytes || hash !== feed.provenance.object.sha256
    || (Object.keys(counts) as Array<keyof typeof counts>).some(key => counts[key] !== feed.provenance[key])) throw new Error('NRC cache source bytes or row accounting differs');
  signal?.throwIfAborted();
  return feed;
}
